// @ts-expect-error: No declarations for @ember/owner
import { type Owner, setOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type ParityInsightSegmentationItem from 'lumen/models/parity/insight/segmentation-item';
import {
  SEGMENTATION_VALUE_TYPES,
  type SegmentationItemLike,
} from 'lumen/models/parity/insight/segmentation-item';
import type {
  PercentagesWithSubscriptions,
  PercentagesWithTotal,
} from 'lumen/utils/constants/pi-constant-values';
import { calculateParityAnalyticsValues } from 'lumen/utils/parity-analytics/calculate-parity-analytics-values';

type BreakdownItemOtherSignature = SegmentationItemLike & {
  id: 'other';
  isExpanded: boolean;
  isSelectable: false;
  segmentationValue: string;
  segmentationValueType: typeof SEGMENTATION_VALUE_TYPES.OTHER;
};

export function isOtherRow(item: unknown): item is BreakdownItemOther {
  return item instanceof BreakdownItemOther;
}

export default class BreakdownItemOther implements BreakdownItemOtherSignature {
  @service declare intl: Services['intl'];

  @tracked isExpanded: boolean;

  children: ParityInsightSegmentationItem[];
  readonly id = 'other';
  readonly isSelectable = false;
  segmentationValueType = SEGMENTATION_VALUE_TYPES.OTHER;

  get calculatedValues(): PercentagesWithTotal {
    return calculateParityAnalyticsValues(this.value);
  }

  get formattedSegmentationValue(): string {
    return this.segmentationValue;
  }

  get segmentationValue(): string {
    return this.intl.t('insight_pages.breakdown.more', {
      count: this.children.length,
    });
  }

  get value(): PercentagesWithSubscriptions {
    const accumulated: PercentagesWithSubscriptions = {
      win: 0,
      meet: 0,
      loss: 0,
      subscriptions: 0,
    };

    this.children.forEach((child) => {
      accumulated.win += child.value.win;
      accumulated.meet += child.value.meet;
      accumulated.loss += child.value.loss;
      accumulated.subscriptions += child.value.subscriptions;
    });

    return accumulated;
  }

  constructor(
    owner: Owner,
    data: {
      children: ParityInsightSegmentationItem[];
      isExpanded: boolean;
    },
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    setOwner(this, owner);
    this.isExpanded = data.isExpanded;
    this.children = data.children;
  }
}
